import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';
import { getISOWeek } from 'date-fns';
import { ApproverLinesCustomPeriod } from '../api/generated/data-contracts';

// hooks to format the approver list details groups: cfr GetApproverListDetailsListGroups utils
export function useApproverListDetailsListGroups(approvalLines: ApproverLinesCustomPeriod[]) {
    const { t } = useTranslation('common');

    // Formats the period name with localized month names and ISO week number
    const getFormattedPeriodName = useCallback(
        (startDate: Date, endDate: Date) => {
            const startMonth = t(`shortMonths.${startDate.getMonth()}`);
            const endMonth = t(`shortMonths.${endDate.getMonth()}`);
            return t('previewPeriodName', {
                startDate: startDate.getDate(),
                startMonth,
                endDate: endDate.getDate(),
                endMonth,
                week: getISOWeek(startDate),
            });
        },
        [t],
    );

    // Groups approval lines by unique period names
    const translatedGroups = useMemo(() => {
        const uniqueLines = Array.from(new Set(approvalLines.map(line => line.periodName)))
            .map(periodName => approvalLines.find(line => line.periodName === periodName))
            .filter(Boolean);

        let currentStartIndex = 0;
        return uniqueLines.map(line => {
            const { periodId, periodState, startDate: rawStart, endDate: rawEnd, periodName } = line;
            const startDate = new Date(rawStart);
            const endDate = new Date(rawEnd);
            const formattedName = `${getFormattedPeriodName(startDate, endDate)} - [${periodState}]`;
            const count = approvalLines.filter(l => l.periodName === periodName).length;
            const startIndex = currentStartIndex;
            currentStartIndex += count;
            return {
                key: periodId,
                name: formattedName,
                startIndex,
                count,
                level: 0,
            };
        });
    }, [approvalLines, getFormattedPeriodName]);

    return translatedGroups;
}
